// src/firebase/testService.js - Test and Question Bank Management
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { db } from './config';

// Create a new test
export const createTest = async (testData, collegeId, collegeName, collegeLogoURL) => {
  try {
    const testDoc = {
      title: testData.title,
      description: testData.description,
      instructions: testData.instructions || 'Read each question carefully and select the correct answer.',
      
      // College info
      collegeId: collegeId,
      collegeName: collegeName,
      collegeLogoURL: collegeLogoURL || '',
      
      // Test configuration
      category: testData.category || 'programming',
      difficulty: testData.difficulty,
      duration: parseInt(testData.duration), // minutes
      passingScore: parseInt(testData.passingScore), // percentage
      certificatePrice: 49, // Default ₹49
      
      // Question configuration
      totalQuestionBank: 0, // Will be updated when questions are added
      questionsToShow: parseInt(testData.questionsToShow),
      questionTypes: ['mcq'],
      
      // Metadata
      isActive: false, // Inactive until questions are added
      participantCount: 0,
      averageScore: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      
      // Topics
      topics: testData.topics || []
    };

    const docRef = await addDoc(collection(db, 'tests'), testDoc);
    console.log('Test created with ID:', docRef.id);
    
    return {
      success: true,
      testId: docRef.id,
      message: 'Test created successfully! Now add questions.'
    };
  } catch (error) {
    console.error('Error creating test:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Add questions to test (creates question bank)
export const addQuestionsToTest = async (testId, questions) => {
  try {
    // Create question bank document
    const questionBankDoc = {
      testId: testId,
      questions: questions.map((q, index) => ({
        id: `q_${Date.now()}_${index}`,
        type: 'mcq',
        question: q.question.trim(),
        options: q.options.map(opt => opt.trim()),
        correctAnswer: parseInt(q.correctAnswer),
        explanation: q.explanation || '',
        difficulty: q.difficulty || 'medium',
        topic: q.topic || '',
        points: 1,
        timeEstimate: 60
      })),
      totalQuestions: questions.length,
      difficultyDistribution: calculateDifficultyDistribution(questions),
      updatedAt: serverTimestamp()
    };

    // Store question bank
    await addDoc(collection(db, 'questionBanks'), questionBankDoc);

    // Update test with question count
    const testRef = doc(db, 'tests', testId);
    await updateDoc(testRef, {
      totalQuestionBank: questions.length,
      isActive: true, // Activate test once questions are added
      updatedAt: serverTimestamp()
    });

    return {
      success: true,
      message: `${questions.length} questions added successfully!`
    };
  } catch (error) {
    console.error('Error adding questions:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Calculate difficulty distribution
const calculateDifficultyDistribution = (questions) => {
  const distribution = { easy: 0, medium: 0, hard: 0 };
  
  questions.forEach(q => {
    const difficulty = q.difficulty || 'medium';
    distribution[difficulty] = (distribution[difficulty] || 0) + 1;
  });
  
  return distribution;
};

// Get all active tests
export const getActiveTests = async () => {
  try {
    const q = query(
      collection(db, 'tests'),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const tests = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return {
      success: true,
      tests: tests
    };
  } catch (error) {
    console.error('Error fetching tests:', error);
    return {
      success: false,
      error: error.message,
      tests: []
    };
  }
};

// Get tests by college
export const getTestsByCollege = async (collegeId) => {
  try {
    console.log('🔍 Fetching tests for college:', collegeId);
    
    // Simple query without orderBy
    const q = query(
      collection(db, 'tests'),
      where('collegeId', '==', collegeId)
    );
    
    const querySnapshot = await getDocs(q);
    console.log('📊 College tests found:', querySnapshot.size);
    
    const tests = [];
    querySnapshot.forEach((doc) => {
      tests.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Sort manually
    tests.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(0);
      const dateB = b.createdAt?.toDate?.() || new Date(0);
      return dateB - dateA;
    });
    
    return {
      success: true,
      tests: tests
    };
  } catch (error) {
    console.error('❌ Error fetching college tests:', error);
    return {
      success: false,
      error: error.message,
      tests: []
    };
  }
};

// Get single test by ID
export const getTestById = async (testId) => {
  try {
    const testRef = doc(db, 'tests', testId);
    const testDoc = await getDoc(testRef);
    
    if (!testDoc.exists()) {
      return {
        success: false,
        error: 'Test not found',
        test: null
      };
    }
    
    return {
      success: true,
      test: {
        id: testDoc.id,
        ...testDoc.data()
      }
    };
  } catch (error) {
    console.error('Error fetching test:', error);
    return {
      success: false,
      error: error.message,
      test: null
    };
  }
};

// Get question bank for test
export const getQuestionBank = async (testId) => {
  try {
    const q = query(
      collection(db, 'questionBanks'),
      where('testId', '==', testId)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return {
        success: false,
        error: 'Question bank not found',
        questionBank: null
      };
    }
    
    const questionBankDoc = querySnapshot.docs[0];
    return {
      success: true,
      questionBank: {
        id: questionBankDoc.id,
        ...questionBankDoc.data()
      }
    };
  } catch (error) {
    console.error('Error fetching question bank:', error);
    return {
      success: false,
      error: error.message,
      questionBank: null
    };
  }
};

// Update test
export const updateTest = async (testId, updates) => {
  try {
    const testRef = doc(db, 'tests', testId);
    await updateDoc(testRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    return {
      success: true,
      message: 'Test updated successfully!'
    };
  } catch (error) {
    console.error('Error updating test:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Delete test
export const deleteTest = async (testId) => {
  try {
    // Delete test document
    await deleteDoc(doc(db, 'tests', testId));
    
    // Delete associated question bank
    const q = query(
      collection(db, 'questionBanks'),
      where('testId', '==', testId)
    );
    const querySnapshot = await getDocs(q);
    
    const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
    
    return {
      success: true,
      message: 'Test deleted successfully!'
    };
  } catch (error) {
    console.error('Error deleting test:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Increment participant count
export const incrementParticipantCount = async (testId) => {
  try {
    const testRef = doc(db, 'tests', testId);
    await updateDoc(testRef, {
      participantCount: increment(1)
    });
  } catch (error) {
    console.error('Error incrementing participant count:', error);
  }
};

// Update average score
export const updateAverageScore = async (testId, newScore) => {
  try {
    const testDoc = await getDoc(doc(db, 'tests', testId));
    if (testDoc.exists()) {
      const testData = testDoc.data();
      const currentAvg = testData.averageScore || 0;
      const count = testData.participantCount || 0;
      
      const newAvg = ((currentAvg * count) + newScore) / (count + 1);
      
      await updateDoc(doc(db, 'tests', testId), {
        averageScore: Math.round(newAvg)
      });
    }
  } catch (error) {
    console.error('Error updating average score:', error);
  }
};

// ============= NEW FUNCTIONS FOR TEST TAKING & CERTIFICATES =============

// Submit test attempt and calculate score
export const submitTestAttempt = async (attemptData) => {
  try {
    console.log('📝 Submitting test attempt...');
    
    // Create attempt document
    const attemptRef = await addDoc(collection(db, 'testAttempts'), {
      ...attemptData,
      submittedAt: serverTimestamp(),
      certificateGenerated: false
    });

    console.log('✅ Test attempt saved:', attemptRef.id);

    // Try to update participant count (non-critical operation)
    try {
      const testRef = doc(db, 'tests', attemptData.testId);
      await updateDoc(testRef, {
        participantCount: increment(1)
      });
      console.log('✅ Participant count updated');
    } catch (countError) {
      // Log but don't fail the entire submission if count update fails
      console.warn('⚠️ Could not update participant count (non-critical):', countError.message);
      // This is okay - the test submission still succeeded
    }

    // Return success regardless of count update
    return {
      success: true,
      attemptId: attemptRef.id,
      message: 'Test submitted successfully!'
    };
  } catch (error) {
    console.error('❌ Error submitting test:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get a specific test attempt (for results page)
export const getTestAttempt = async (attemptId) => {
  try {
    const attemptRef = doc(db, 'testAttempts', attemptId);
    const attemptDoc = await getDoc(attemptRef);
    
    if (!attemptDoc.exists()) {
      return {
        success: false,
        error: 'Test attempt not found'
      };
    }

    return {
      success: true,
      attempt: { id: attemptDoc.id, ...attemptDoc.data() }
    };
  } catch (error) {
    console.error('Error fetching test attempt:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Verify certificate by attempt ID (for public verification)
export const verifyCertificate = async (attemptId) => {
  try {
    const attemptRef = doc(db, 'testAttempts', attemptId);
    const attemptDoc = await getDoc(attemptRef);
    
    if (!attemptDoc.exists()) {
      return {
        success: false,
        error: 'Certificate not found',
        valid: false
      };
    }

    const data = attemptDoc.data();
    
    return {
      success: true,
      valid: data.passed,
      data: {
        studentName: data.studentName,
        studentEmail: data.studentEmail,
        testTitle: data.testTitle,
        collegeName: data.collegeName,
        score: data.score,
        correctAnswers: data.correctAnswers,
        totalQuestions: data.totalQuestions,
        submittedAt: data.submittedAt,
        passed: data.passed
      }
    };
  } catch (error) {
    console.error('Error verifying certificate:', error);
    return {
      success: false,
      error: error.message,
      valid: false
    };
  }
};

// Get question bank with questions for test taking
export const getQuestionsForTest = async (testId) => {
  try {
    const q = query(
      collection(db, 'questionBanks'),
      where('testId', '==', testId)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return {
        success: false,
        error: 'No questions found for this test',
        questions: []
      };
    }
    
    const questionBankDoc = querySnapshot.docs[0];
    const questionBank = questionBankDoc.data();
    
    return {
      success: true,
      questions: questionBank.questions || []
    };
  } catch (error) {
    console.error('Error fetching questions:', error);
    return {
      success: false,
      error: error.message,
      questions: []
    };
  }
};

// Get student's test attempts
export const getTestAttempts = async (testId) => {
  try {
    console.log('🔍 Fetching attempts for test:', testId);
    
    const q = query(
      collection(db, 'testAttempts'),
      where('testId', '==', testId)
    );
    
    const querySnapshot = await getDocs(q);
    console.log('📊 Test attempts found:', querySnapshot.size);
    
    const attempts = [];
    querySnapshot.forEach((doc) => {
      attempts.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Sort by date (newest first)
    attempts.sort((a, b) => {
      const dateA = a.submittedAt?.toDate?.() || new Date(0);
      const dateB = b.submittedAt?.toDate?.() || new Date(0);
      return dateB - dateA;
    });
    
    return {
      success: true,
      attempts: attempts
    };
  } catch (error) {
    console.error('❌ Error fetching test attempts:', error);
    return {
      success: false,
      error: error.message,
      attempts: []
    };
  }
};

// Update questions in question bank
export const updateQuestions = async (testId, questions) => {
  try {
    // Find the question bank document for this test
    const q = query(
      collection(db, 'questionBanks'),
      where('testId', '==', testId)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      // Create new question bank if it doesn't exist
      const questionBankDoc = {
        testId: testId,
        questions: questions.map((q, index) => ({
          id: q.id || `q_${Date.now()}_${index}`,
          type: 'mcq',
          question: q.question.trim(),
          options: q.options.map(opt => opt.trim()),
          correctAnswer: parseInt(q.correctAnswer),
          explanation: q.explanation || '',
          difficulty: q.difficulty || 'medium',
          topic: q.topic || '',
          points: 1,
          timeEstimate: 60
        })),
        totalQuestions: questions.length,
        difficultyDistribution: calculateDifficultyDistribution(questions),
        updatedAt: serverTimestamp()
      };
      
      await addDoc(collection(db, 'questionBanks'), questionBankDoc);
    } else {
      // Update existing question bank
      const questionBankRef = querySnapshot.docs[0].ref;
      await updateDoc(questionBankRef, {
        questions: questions.map((q, index) => ({
          id: q.id || `q_${Date.now()}_${index}`,
          type: 'mcq',
          question: q.question.trim(),
          options: q.options.map(opt => opt.trim()),
          correctAnswer: parseInt(q.correctAnswer),
          explanation: q.explanation || '',
          difficulty: q.difficulty || 'medium',
          topic: q.topic || '',
          points: 1,
          timeEstimate: 60
        })),
        totalQuestions: questions.length,
        difficultyDistribution: calculateDifficultyDistribution(questions),
        updatedAt: serverTimestamp()
      });
    }
    
    // Update test's totalQuestionBank
    const testRef = doc(db, 'tests', testId);
    await updateDoc(testRef, {
      totalQuestionBank: questions.length,
      updatedAt: serverTimestamp()
    });
    
    return {
      success: true,
      message: 'Questions updated successfully!'
    };
  } catch (error) {
    console.error('Error updating questions:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get student profile data
export const getStudentProfile = async (studentId) => {
  try {
    // Get user data
    const userRef = doc(db, 'users', studentId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return {
        success: false,
        error: 'Student not found'
      };
    }
    
    const userData = userDoc.data();
    
    // Get all test attempts by this student
    const attemptsQuery = query(
      collection(db, 'testAttempts'),
      where('studentId', '==', studentId)
    );
    
    const attemptsSnapshot = await getDocs(attemptsQuery);
    const attempts = [];
    
    attemptsSnapshot.forEach((doc) => {
      attempts.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Sort by date
    attempts.sort((a, b) => {
      const dateA = a.submittedAt?.toDate?.() || new Date(0);
      const dateB = b.submittedAt?.toDate?.() || new Date(0);
      return dateB - dateA;
    });
    
    // Calculate statistics
    const totalTests = attempts.length;
    const passedTests = attempts.filter(a => a.passed).length;
    const avgScore = totalTests > 0 
      ? Math.round(attempts.reduce((sum, a) => sum + a.score, 0) / totalTests)
      : 0;
    
    return {
      success: true,
      student: {
        ...userData,
        id: studentId,
        statistics: {
          totalTests,
          passedTests,
          failedTests: totalTests - passedTests,
          avgScore,
          passRate: totalTests > 0 ? Math.round((passedTests / totalTests) * 100) : 0
        },
        attempts
      }
    };
  } catch (error) {
    console.error('Error fetching student profile:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get student's test attempts (used in StudentDashboard)
export const getStudentAttempts = async (studentId) => {
  try {
    console.log('🔍 Fetching attempts for student:', studentId);
    
    const q = query(
      collection(db, 'testAttempts'),
      where('studentId', '==', studentId)
    );
    
    const querySnapshot = await getDocs(q);
    console.log('📊 Found attempts:', querySnapshot.size);
    
    const attempts = [];
    querySnapshot.forEach((doc) => {
      attempts.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Sort manually by submittedAt (newest first)
    attempts.sort((a, b) => {
      const dateA = a.submittedAt?.toDate?.() || new Date(0);
      const dateB = b.submittedAt?.toDate?.() || new Date(0);
      return dateB - dateA;
    });
    
    return {
      success: true,
      attempts: attempts
    };
  } catch (error) {
    console.error('❌ Error fetching student attempts:', error);
    return {
      success: false,
      error: error.message,
      attempts: []
    };
  }
};

const testService = {
  createTest,
  addQuestionsToTest,
  getActiveTests,
  getTestsByCollege,
  getTestById,
  getQuestionBank,
  updateTest,
  deleteTest,
  incrementParticipantCount,
  updateAverageScore,
  // NEW FUNCTIONS
  submitTestAttempt,
  getTestAttempt,
  verifyCertificate,
  getQuestionsForTest,
  getStudentAttempts,
  getStudentProfile,
  getTestAttempts,
  updateQuestions
};

export default testService;